/**
 * publishHeightVar - keep a CSS custom property on <html> equal to an element's height, without
 * feeding the browser a ResizeObserver loop.
 *
 * Writing a variable that the whole page's layout reads (the banner stack height feeds `--app-vh`,
 * which sizes every full-height layout) from INSIDE a ResizeObserver callback resizes other observed
 * elements in the same delivery. The browser then drops their notifications and reports
 * "ResizeObserver loop completed with undelivered notifications"; on the call page it fired on every
 * frame a banner (the "You're offline. Reconnecting..." bar) moved, and every observer on the page
 * re-ran (React #185 investigation, 2026-10-24). So:
 *
 *   - the observer only schedules: the write happens in the next animation frame, before that
 *     frame's layout, where every observer sees the change in its normal pass;
 *   - one frame per burst of notifications, however many arrive;
 *   - nothing is written when the value is unchanged.
 *
 * The first value is published synchronously (mount time, outside any observer delivery) so the
 * page lays out with the right space on its first paint.
 */

export interface PublishHeightVarOptions {
  /** Defaults to the element's border-box height. */
  measure?: (el: HTMLElement) => number;
  /** Where the variable lives (default document.documentElement). */
  target?: HTMLElement;
}

/** Starts publishing; returns a disposer that stops observing and cancels a pending frame. */
export function publishHeightVar(
  el: HTMLElement,
  varName: string,
  { measure = (node) => node.getBoundingClientRect().height, target }: PublishHeightVarOptions = {},
): () => void {
  const root = target ?? document.documentElement;
  let last: string | null = null;
  let frame = 0;

  const write = () => {
    frame = 0;
    const next = `${measure(el)}px`;
    if (next === last) return;
    last = next;
    root.style.setProperty(varName, next);
  };

  write();
  if (typeof ResizeObserver === 'undefined') return () => {};

  const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : (cb: FrameRequestCallback) => setTimeout(() => cb(0), 16) as unknown as number;
  const caf = typeof cancelAnimationFrame === 'function' ? cancelAnimationFrame : (id: number) => clearTimeout(id);
  const observer = new ResizeObserver(() => {
    if (!frame) frame = raf(write);
  });
  observer.observe(el);
  return () => {
    observer.disconnect();
    if (frame) caf(frame);
    frame = 0;
  };
}
